import type {
  CircleInvite,
  Cohort,
  CohortMemberEntry,
  CohortSession,
  CohortTestRoom,
  CohortTestRoomBot,
  CreateCohortInput,
  RsvpResponse,
  RsvpStatus,
  SessionNoteResponse,
  UpdateCohortInput,
} from '../types';
import api from './api';

export type { CreateCohortInput } from '../types';

export const getUserCohorts = async (): Promise<Cohort[]> => {
  const response = await api.get('/cohorts');
  return response.data.data;
};

export const getPublicCohorts = async (
  params?: Record<string, string>,
): Promise<{ data: Cohort[]; total: number }> => {
  const response = await api.get('/cohorts/public', { params });
  return response.data.data;
};

export const getCohort = async (id: number): Promise<Cohort> => {
  const response = await api.get(`/cohorts/${id}`);
  return response.data.data;
};

export const createCohort = async (data: CreateCohortInput): Promise<Cohort> => {
  const response = await api.post('/cohorts', data);
  return response.data.data;
};

export const updateCohort = async (id: number, data: UpdateCohortInput): Promise<Cohort> => {
  const response = await api.put(`/cohorts/${id}`, data);
  return response.data.data;
};

export const deleteCohort = async (id: number): Promise<void> => {
  await api.delete(`/cohorts/${id}`);
};

export const joinCohort = async (id: number, inviteCode?: string): Promise<CohortMemberEntry> => {
  const response = await api.post(
    `/cohorts/${id}/join`,
    inviteCode ? { invite_code: inviteCode } : {},
  );
  return response.data.data;
};

export const leaveCohort = async (id: number): Promise<void> => {
  await api.post(`/cohorts/${id}/leave`);
};

/**
 * Remove them from the GROUP. Membership ends; they may rejoin or ask to,
 * depending on the group's settings. Not the same as removing them from a
 * session that is running — see removeFromSession.
 */
export const removeFromGroup = async (cohortId: number, userId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/kick`, { user_id: userId });
};

/**
 * Remove them from THIS SESSION. They stay a member of the group and cannot
 * enter its sessions for 24 hours.
 *
 * This is the one an organizer reaches for mid-call, and until now the button
 * that looked like it called this one actually called removeFromGroup, which
 * ended somebody's membership outright.
 */
export const removeFromSession = async (circleId: number, userId: number): Promise<void> => {
  await api.post(`/circles/${circleId}/kick`, { user_id: userId });
};

/**
 * Ban them from the group, for good. Membership ends, they cannot ask to come
 * back, and they are taken out of any session running now.
 */
export const banMember = async (
  cohortId: number,
  userId: number,
  reason: string,
): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/ban`, { user_id: userId, reason });
};

export const promoteMember = async (cohortId: number, userId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/promote`, { user_id: userId });
};

export const demoteMember = async (cohortId: number, userId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/demote`, { user_id: userId });
};

export const regenerateInviteCode = async (cohortId: number): Promise<string> => {
  const response = await api.post(`/cohorts/${cohortId}/regenerate-invite`);
  return response.data.data.invite_code;
};

export const getCohortSessions = async (
  cohortId: number,
  status?: string,
  limit?: number,
): Promise<CohortSession[]> => {
  const response = await api.get(`/cohorts/${cohortId}/sessions`, {
    params: { ...(status && { status }), ...(limit && { limit }) },
  });
  const payload = response.data.data;
  // Backend returns paginated response — extract items array
  return Array.isArray(payload) ? payload : (payload.data ?? []);
};

export const createCohortSession = async (
  cohortId: number,
  scheduledAt?: string,
): Promise<CohortSession> => {
  const response = await api.post(
    `/cohorts/${cohortId}/sessions`,
    scheduledAt ? { scheduled_at: scheduledAt } : {},
  );
  return response.data.data;
};

export const cancelCohortSession = async (cohortId: number, circleId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/sessions/${circleId}/cancel`);
};

/**
 * Answer "Will you be there?" for one upcoming session. Always sends an
 * explicit status; the server still accepts an empty body as the old toggle
 * only for installed mobile builds that predate this.
 */
export const rsvpToSession = async (
  cohortId: number,
  sessionId: number,
  status: RsvpStatus,
): Promise<RsvpResponse> => {
  const response = await api.post(`/cohorts/${cohortId}/sessions/${sessionId}/rsvp`, { status });
  return response.data.data as RsvpResponse;
};

/**
 * Create or edit the session note. Refusals carry `error`: not_organizer,
 * not_found, editing_closed (the session started), unavailable.
 */
export const saveSessionNote = async (
  cohortId: number,
  sessionId: number,
  input: { theme_question: string; body: string | null },
): Promise<SessionNoteResponse> => {
  const response = await api.put(`/cohorts/${cohortId}/sessions/${sessionId}/note`, input);
  return response.data.data as SessionNoteResponse;
};

export const pauseCohort = async (id: number): Promise<void> => {
  await api.post(`/cohorts/${id}/pause`);
};

export const resumeCohort = async (id: number): Promise<void> => {
  await api.post(`/cohorts/${id}/resume`);
};

// Pending member management
export const getPendingMembers = async (cohortId: number): Promise<CohortMemberEntry[]> => {
  const response = await api.get(`/cohorts/${cohortId}/pending`);
  return response.data.data;
};

export const approveMember = async (cohortId: number, userId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/members/${userId}/approve`);
};

export const declineMember = async (cohortId: number, userId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/members/${userId}/decline`);
};

export const getMyJournal = async (
  circleId: number,
): Promise<{ id: number; circle_id: number; entry: string; created_at: string } | null> => {
  const response = await api.get(`/circles/${circleId}/my-journal`);
  return response.data.data;
};

export const checkPrerequisites = async (
  cohortId: number,
): Promise<{
  prerequisites: Array<{ type: string; value: string | number; met: boolean; description: string }>;
  all_met: boolean;
}> => {
  const response = await api.get(`/cohorts/${cohortId}/prerequisites-check`);
  return response.data.data;
};

export const optOutSession = async (cohortId: number, circleId: number): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/sessions/${circleId}/opt-out`);
};

export const rescheduleSession = async (
  cohortId: number,
  circleId: number,
  scheduledAt: string,
): Promise<{ id: number; scheduled_at: string }> => {
  const response = await api.post(`/cohorts/${cohortId}/sessions/${circleId}/reschedule`, {
    scheduled_at: scheduledAt,
  });
  return response.data.data;
};

// Organizer test rooms ("Preview your session") — see docs/plans/FACILITATOR-TOOLS.md §3
export const launchTestRoom = async (
  cohortId: number,
  /** Test participants to start with. Omit for the sensible default (a
   *  populated room); pass 0 to see the true empty state a member walks into. */
  withParticipants?: number,
): Promise<{ circle_id: number; expires_at: string | null }> => {
  const response = await api.post(
    `/cohorts/${cohortId}/test-room`,
    withParticipants === undefined ? {} : { with_participants: withParticipants },
  );
  return response.data.data;
};

export const getTestRooms = async (cohortId: number): Promise<CohortTestRoom[]> => {
  const response = await api.get(`/cohorts/${cohortId}/test-rooms`);
  return response.data.data;
};

export const endTestRoom = async (cohortId: number, circleId: number): Promise<void> => {
  await api.delete(`/cohorts/${cohortId}/test-room/${circleId}`);
};

export const addTestParticipants = async (
  cohortId: number,
  circleId: number,
  count: number,
): Promise<{ added: CohortTestRoomBot[]; bots: CohortTestRoomBot[] }> => {
  const response = await api.post(`/cohorts/${cohortId}/test-room/${circleId}/participants`, {
    count,
  });
  return response.data.data;
};

// Discovery
export const discoverCircles = async (): Promise<{ cohorts: Cohort[] }> => {
  const response = await api.get('/circles/discover');
  return response.data.data;
};

export const getMyCircles = async (): Promise<{ cohorts: Cohort[] }> => {
  const response = await api.get('/circles/my-circles');
  return response.data.data;
};

// Invites
export const searchFollowers = async (
  search: string,
): Promise<Array<{ id: number; name: string; username: string; avatar_url: string | null }>> => {
  const response = await api.get('/users/followers', { params: { search } });
  return response.data.data;
};

export const sendInvite = async (cohortId: number, inviteeId: number): Promise<CircleInvite> => {
  const response = await api.post(`/cohorts/${cohortId}/invite`, { invitee_id: inviteeId });
  return response.data.data;
};

export const respondToInvite = async (
  cohortId: number,
  response: 'accepted' | 'declined',
): Promise<void> => {
  await api.post(`/cohorts/${cohortId}/invite/respond`, { response });
};

export const getPendingInvites = async (): Promise<CircleInvite[]> => {
  const response = await api.get('/circles/invites');
  return response.data.data;
};

export const findByInviteCode = async (inviteCode: string): Promise<Cohort> => {
  const response = await api.get(`/cohorts/by-invite/${inviteCode}`);
  return response.data.data;
};
